import axios from "axios";
import { supabase } from "../supabase";
import { getCurrentProductId } from "../product/productStorage";

// The backend's public HTTPS address, set as REACT_APP_API_URL when the site is
// built. Only local development falls back to the backend on this machine:
// a live site calling 127.0.0.1 makes Chrome ask visitors for permission to
// "access other devices on your local network", and the calls fail anyway.
// Without the variable a production build calls /api on its own domain.
const API_URL = (process.env.REACT_APP_API_URL
    || (process.env.NODE_ENV === "development" ? "http://127.0.0.1:3001" : "")).replace(/\/+$/, "");

axios.defaults.baseURL = `${API_URL}/api`;

// Data belongs to the signed-in user, so send their Supabase token, and to the
// selected product, so the backend scopes memory, plans and links to it
const authHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const productId = getCurrentProductId();
    return {
        ...(session && { Authorization: `Bearer ${session.access_token}` }),
        ...(productId && { 'X-Product-Id': productId }),
    };
};

const withAuth = async (config = {}) => ({ ...config, headers: { ...config.headers, ...(await authHeaders()) } });

// Plan limits answer 402. The billing provider listens for this event and shows
// the upgrade prompt, wherever in the app the request came from.
export const LIMIT_EVENT = 'ls:limit-reached';

// The upgrade prompt already explains a plan limit, so screens skip their own error for it
export const isLimitError = (error) => error?.response?.status === 402;

// arraybuffer responses (images, video) arrive as bytes, so decode the JSON error
const errorBody = (data) => {
    if (data instanceof ArrayBuffer) {
        try {
            return JSON.parse(new TextDecoder().decode(data));
        } catch (e) {
            return {};
        }
    }
    return data || {};
};

// The API only answers JSON. A web page back means the request reached the site
// itself (usually REACT_APP_API_URL isn't set where the site is built), so treat
// it as a failed request instead of handing screens HTML as data.
const isWebPage = (response) => {
    if (response.config?.responseType && response.config.responseType !== 'json') return false;
    const type = String(response.headers?.['content-type'] || '');
    return type.includes('text/html') || (typeof response.data === 'string' && /^\s*</.test(response.data));
};

axios.interceptors.response.use((response) => {
    if (!isWebPage(response)) return response;
    const error = new Error(`Expected JSON from ${response.config?.baseURL || ''}${response.config?.url || ''} but got a web page. Is REACT_APP_API_URL set to the backend's address?`);
    error.config = response.config;
    return Promise.reject(error);
}, (error) => {
    if (error.response?.status === 402) {
        const detail = errorBody(error.response.data);
        error.response.data = detail;
        error.limit = detail;
        window.dispatchEvent(new CustomEvent(LIMIT_EVENT, { detail }));
    }
    return Promise.reject(error);
});

const api = {
    getChatCompletion: async (userMessage) => {
        try {
            const response = await axios.post('/getChatCompletion', {
                userMessage
            }, await withAuth());
            return response.data.content;
        } catch (error) {
            console.error('Error getting chat completion:', error);
            throw error;
        }
    },

    generateImage: async (prompt) => {
        //return axios.post("/recipeImageFluxSchnell", recipeName);
        try {
            const response = await axios.post('/generateImage', { prompt: prompt }, await withAuth({
                responseType: 'arraybuffer', // Important to receive binary data
            }));
            console.log(response.data);
            return response.data; // This will be an ArrayBuffer
        } catch (error) {
            console.error(error);
            throw error;
        }
    },



    // Weekly plans with targets, stored in the AI's memory files
    listPlanWeeks: async () => {
        const response = await axios.get('/plans', { headers: await authHeaders() });
        return response.data.weeks;
    },

    // Resolves to null when the week has no plan yet
    getPlan: async (weekStart) => {
        try {
            const response = await axios.get(`/plans/${weekStart}`, { headers: await authHeaders() });
            return response.data;
        } catch (error) {
            if (error.response?.status === 404) return null;
            throw error;
        }
    },

    generatePlan: async (weekStart, preferences) => {
        const response = await axios.post(`/plans/${weekStart}/generate`, { preferences }, {
            headers: await authHeaders(),
        });
        return response.data;
    },

    publishPlanPost: async (weekStart, postId, destination) => {
        const response = await axios.post(`/plans/${weekStart}/posts/${postId}/publish`, { destination }, {
            headers: await authHeaders(),
        });
        return response.data;
    },

    // actuals: { [postId]: { views, likes } }
    reviewPlan: async (weekStart, actuals, today) => {
        const response = await axios.post(`/plans/${weekStart}/review`, { actuals, today }, {
            headers: await authHeaders(),
        });
        return response.data;
    },

    listMemoryFiles: async () => {
        const response = await axios.get('/memory', { headers: await authHeaders() });
        return response.data.files;
    },

    getMemoryFile: async (path) => {
        const response = await axios.get('/memory/file', { params: { path }, headers: await authHeaders() });
        return response.data;
    },

    saveMemoryFile: async (path, content) => {
        const response = await axios.put('/memory/file', { path, content }, { headers: await authHeaders() });
        return response.data;
    },

    deleteMemoryFile: async (path) => {
        await axios.delete('/memory/file', { params: { path }, headers: await authHeaders() });
    },

    // Text (and optional start image) to video via OpenRouter. Returns an ArrayBuffer of an mp4.
    generateVideo: async ({ prompt, aspectRatio, duration, image, premium, generateAudio }) => {
        try {
            const response = await axios.post('/generateVideo', {
                prompt, aspectRatio, duration, image, premium, generateAudio
            }, await withAuth({
                responseType: 'arraybuffer',
                timeout: 10 * 60 * 1000, // Video generation can take a few minutes
            }));
            return response.data;
        } catch (error) {
            console.error('Error generating video:', error);
            throw error;
        }
    },

    createTrackedLink: async ({ destination, platform, campaign, hook, icpId, contentType }) => {
        const response = await axios.post('/links', { destination, platform, campaign, hook, icpId, contentType }, {
            headers: await authHeaders(),
        });
        return response.data;
    },

    listTrackedLinks: async () => {
        const response = await axios.get('/links', { headers: await authHeaders() });
        return response.data.links;
    },

    deleteTrackedLink: async (slug) => {
        await axios.delete(`/links/${slug}`, { headers: await authHeaders() });
    },

    // Products: everything the AI knows about what's being promoted
    listProducts: async () => {
        const response = await axios.get('/products', await withAuth());
        return response.data.products;
    },

    getProduct: async (id) => {
        const response = await axios.get(`/products/${id}`, await withAuth());
        return response.data;
    },

    createProduct: async (product) => {
        const response = await axios.post('/products', product, await withAuth());
        return response.data;
    },

    updateProduct: async (id, changes) => {
        const response = await axios.patch(`/products/${id}`, changes, await withAuth());
        return response.data;
    },

    deleteProduct: async (id) => {
        await axios.delete(`/products/${id}`, await withAuth());
    },

    // Just the icon of a landing page or app store listing: { icon }
    findProductIcon: async (type, url) => {
        const response = await axios.post('/products/icon', { type, url }, await withAuth({ timeout: 45 * 1000 }));
        return response.data;
    },

    // Fetches a landing page or app store listing: { title, detail, url, icon, text }
    readSource: async (type, url) => {
        const response = await axios.post('/products/sources/read', { type, url }, await withAuth({ timeout: 60 * 1000 }));
        return response.data;
    },

    // Extracts product fields from sources: { fields, sourceCounts, questions }
    extractProduct: async (sources) => {
        const response = await axios.post('/products/extract', { sources }, await withAuth({ timeout: 3 * 60 * 1000 }));
        return response.data;
    },

    resyncProduct: async (id) => {
        const response = await axios.post(`/products/${id}/resync`, {}, await withAuth({ timeout: 3 * 60 * 1000 }));
        return response.data;
    },

    listIcps: async (productId) => {
        const response = await axios.get(`/products/${productId}/icps`, await withAuth());
        return response.data.icps;
    },

    replaceIcps: async (productId, icps) => {
        const response = await axios.put(`/products/${productId}/icps`, { icps }, await withAuth());
        return response.data.icps;
    },

    createIcp: async (productId, icp) => {
        const response = await axios.post(`/products/${productId}/icps`, icp, await withAuth());
        return response.data;
    },

    updateIcp: async (productId, icpId, changes) => {
        const response = await axios.patch(`/products/${productId}/icps/${icpId}`, changes, await withAuth());
        return response.data;
    },

    deleteIcp: async (productId, icpId) => {
        await axios.delete(`/products/${productId}/icps/${icpId}`, await withAuth());
    },

    // Drafts ICPs (not saved). With `describe`, drafts one ICP from that description.
    generateIcps: async (productId, { answers, describe } = {}) => {
        const response = await axios.post(`/products/${productId}/icps/generate`, { answers, describe }, await withAuth({ timeout: 2 * 60 * 1000 }));
        return response.data.icps;
    },

    suggestVoice: async (productId) => {
        const response = await axios.post(`/products/${productId}/voice/suggest`, {}, await withAuth({ timeout: 2 * 60 * 1000 }));
        return response.data;
    },

    // Three ready-to-post pieces for the primary ICP, at the end of onboarding: { icpId, posts }
    writeFirstPosts: async (productId, icpId) => {
        const response = await axios.post(`/products/${productId}/first-posts`, { icpId }, await withAuth({ timeout: 2 * 60 * 1000 }));
        return response.data;
    },

    // Landing page, no sign in: { email, name?, website?, plan?, interval?, source?, company? } → { joined, already }
    joinWaitlist: async (entry) => {
        const response = await axios.post('/waitlist', entry);
        return response.data;
    },

    // Billing. Pricing is public: the landing page shows it too.
    getPricing: async ({ currency, locale } = {}) => {
        const response = await axios.get('/billing/pricing', { params: { currency, locale } });
        return response.data;
    },

    // { billingEnabled, account, credits, usage }
    getBilling: async () => {
        const response = await axios.get('/billing', await withAuth());
        return response.data;
    },

    // Resolve to { url } of a Stripe page to send the user to
    startCheckout: async ({ plan, interval, currency, returnPath }) => {
        const response = await axios.post('/billing/checkout', { plan, interval, currency, returnPath }, await withAuth());
        return response.data;
    },

    buyPack: async ({ pack, currency, returnPath }) => {
        const response = await axios.post('/billing/packs/checkout', { pack, currency, returnPath }, await withAuth());
        return response.data;
    },

    openBillingPortal: async (flow) => {
        const response = await axios.post('/billing/portal', { flow }, await withAuth());
        return response.data;
    },

    // These resolve to { account }
    changePlan: async ({ plan, interval }) => {
        const response = await axios.post('/billing/change-plan', { plan, interval }, await withAuth());
        return response.data;
    },

    acceptRetentionOffer: async (offer) => {
        const response = await axios.post('/billing/retention', { offer }, await withAuth());
        return response.data;
    },

    cancelPlan: async ({ reason, details }) => {
        const response = await axios.post('/billing/cancel', { reason, details }, await withAuth());
        return response.data;
    },

    resumePlan: async () => {
        const response = await axios.post('/billing/resume', {}, await withAuth());
        return response.data;
    },

    retryPayment: async () => {
        const response = await axios.post('/billing/retry-payment', {}, await withAuth());
        return response.data;
    },

    // Permanently deletes the account, its data and its Stripe customer
    deleteAccount: async () => {
        await axios.delete('/account', await withAuth({ data: { confirm: 'DELETE' } }));
    },

    generateContentDescription: async (userMessage) => {
        try {
            const response = await axios.post('/generateContentDescription', {
                userMessage
            }, await withAuth());
            return response.data.content;
        } catch (error) {
            console.error('Error generating content description:', error);
            throw error;
        }
    },

    // Email updates: which emails the user gets, when, and a test send of one
    getNotificationSettings: async () => {
        const response = await axios.get('/notifications/settings', await withAuth());
        return response.data;
    },

    updateNotificationSettings: async (changes) => {
        const response = await axios.put('/notifications/settings', changes, await withAuth());
        return response.data.settings;
    },

    sendTestEmail: async (kind) => {
        const response = await axios.post('/notifications/test', { kind }, await withAuth());
        return response.data;
    },

    // Hooks, caption and hashtags for one planned post (again, to rewrite it). Resolves to the full plan.
    draftPlanPost: async (weekStart, postId) => {
        const response = await axios.post(`/plans/${weekStart}/posts/${encodeURIComponent(postId)}/draft`, {}, await withAuth({ timeout: 2 * 60 * 1000 }));
        return response.data;
    },

    // changes: { draft: { hook?, caption?, hashtags? } }. Resolves to the full plan.
    updatePlanPost: async (weekStart, postId, changes) => {
        const response = await axios.patch(`/plans/${weekStart}/posts/${encodeURIComponent(postId)}`, changes, await withAuth());
        return response.data;
    },

    // Social accounts for the current product: { providers, accounts }
    getSocialAccounts: async () => {
        const response = await axios.get('/social/accounts', await withAuth());
        return response.data;
    },

    // Starts OAuth: resolves to { url } to send the browser to
    connectSocial: async (platform) => {
        const response = await axios.post(`/social/${encodeURIComponent(platform)}/connect`, {}, await withAuth());
        return response.data;
    },

    // Links an account by handle, without OAuth. Resolves to { account }
    addManualSocial: async ({ platform, handle, profileUrl }) => {
        const response = await axios.post('/social/manual', { platform, handle, profileUrl }, await withAuth());
        return response.data;
    },

    deleteSocialAccount: async (id) => {
        await axios.delete(`/social/accounts/${encodeURIComponent(id)}`, await withAuth());
    },

    // Topic ideas for the Content plan. The kept list (no generation): { ideas, weekStart?, createdAt? }
    getIdeas: async () => {
        const response = await axios.get('/ideas', await withAuth());
        return response.data;
    },

    // 8-12 new ideas from results, upcoming dates and pillars (uses a generation); same shape as getIdeas
    generateIdeas: async (weekStart) => {
        const response = await axios.post('/ideas', { weekStart }, await withAuth({ timeout: 2 * 60 * 1000 }));
        return response.data;
    },

    // Adds a planned post without the AI: { title, angle, hook, why, day, time, platform, contentType, origin }.
    // Sends the browser's time zone so "this week" matches the user's. Resolves to the full plan.
    addPlanPost: async (weekStart, post) => {
        let tz;
        try {
            tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch (e) {
            tz = undefined;
        }
        const response = await axios.post(`/plans/${weekStart}/posts`, { ...post, tz }, await withAuth());
        return response.data;
    },

    // Turns a post, idea or text into 'carousel' | 'script' | 'ad' | 'blog' | 'thread' (uses a generation).
    // Resolves to { format, title, parts: [{ heading?, body }], cta?, hashtags? }
    repurpose: async (source, to) => {
        const response = await axios.post('/repurpose', { source, to }, await withAuth({ timeout: 2 * 60 * 1000 }));
        return response.data;
    },

    // When tracked links get clicked most, per platform: { enough, clicks, minimum, timezone, platforms, overall }
    getBestTimes: async (tz) => {
        const response = await axios.get('/insights/best-times', await withAuth({ params: { tz } }));
        return response.data;
    },

    // Publishing. What each platform takes: { platforms: { instagram: { phase, needsMedia, maxImages, video, textLimit, links, needsBoard? } } }
    getPublishCapabilities: async () => {
        const response = await axios.get('/publish/capabilities', await withAuth());
        return response.data;
    },

    // Starts an upload: resolves to { assetId, uploadUrl, method, headers }
    createMediaUpload: async ({ name, mimeType, bytes }) => {
        const response = await axios.post('/media/uploads', { name, mimeType, bytes }, await withAuth());
        return response.data;
    },

    // Sends the file to the upload URL with XMLHttpRequest, for progress (0..1). The URL is
    // signed, so no auth headers go with it. Resolves when the upload is done; `signal` aborts it.
    uploadMediaFile: (upload, blob, { onProgress, signal } = {}) => new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const url = /^https?:\/\//i.test(upload.uploadUrl) ? upload.uploadUrl : `${API_URL}${upload.uploadUrl.startsWith('/') ? '' : '/'}${upload.uploadUrl}`;
        xhr.open(upload.method || 'PUT', url);
        Object.entries(upload.headers || {}).forEach(([name, value]) => {
            // The browser sets these itself and refuses to have them set
            if (!/^(content-length|host)$/i.test(name)) xhr.setRequestHeader(name, value);
        });
        xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(Object.assign(new Error(`Upload failed (${xhr.status})`), { status: xhr.status })));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.onabort = () => reject(Object.assign(new Error('Upload cancelled'), { aborted: true }));
        signal?.addEventListener('abort', () => xhr.abort());
        xhr.send(blob);
    }),

    // Resolves to { asset: { id, url, mimeType, kind, width, height, durationSec, bytes } }
    completeMediaUpload: async (assetId) => {
        const response = await axios.post(`/media/uploads/${encodeURIComponent(assetId)}/complete`, {}, await withAuth({ timeout: 2 * 60 * 1000 }));
        return response.data;
    },

    deleteMedia: async (assetId) => {
        await axios.delete(`/media/${encodeURIComponent(assetId)}`, await withAuth());
    },

    // { accountIds, text, mediaIds, scheduledAt (ISO or null for now), planWeek?, planPostId?, options? }.
    // Resolves to { jobs }; a 400 carries { error, details: [{ accountId, errors }] }.
    publish: async (request) => {
        const response = await axios.post('/publish', request, await withAuth());
        return response.data;
    },

    // { planWeek?, status?, limit? } → jobs, newest first
    listPublishJobs: async (params = {}) => {
        const response = await axios.get('/publish/jobs', await withAuth({ params }));
        return response.data.jobs;
    },

    cancelPublishJob: async (id) => {
        const response = await axios.post(`/publish/jobs/${encodeURIComponent(id)}/cancel`, {}, await withAuth());
        return response.data.job;
    },

    retryPublishJob: async (id) => {
        const response = await axios.post(`/publish/jobs/${encodeURIComponent(id)}/retry`, {}, await withAuth());
        return response.data.job;
    },

    // A Pinterest account's boards: [{ id, name }]
    getPinterestBoards: async (accountId) => {
        const response = await axios.get(`/social/accounts/${encodeURIComponent(accountId)}/boards`, await withAuth());
        return response.data.boards;
    },
}

export default api;